export enum CommissionRuleType {
  PLAN = 'PLAN',
  AMOUNT = 'AMOUNT',
  PROMOTION = 'PROMOTION',
}

export enum CommissionStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  PAID = 'PAID',
  REVERSED = 'REVERSED',
}

export enum CommissionChangeType {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  DELETED = 'DELETED',
}

export enum CommissionEntityType {
  CONFIG = 'CONFIG',
  RULE = 'RULE',
}

export interface RuleCriteria {
  planId?: string;
  minAmount?: number;
  maxAmount?: number;
  planIds?: string[];
}

export interface CommissionConfigDto {
  id: string;
  gymId: string;
  defaultPercentage: number;
  reversalPeriodDays: number;
  minimumContractDays: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCommissionConfigDto {
  defaultPercentage: number;
  reversalPeriodDays: number;
  minimumContractDays: number;
  isActive: boolean;
}

export interface UpdateCommissionConfigDto {
  defaultPercentage?: number;
  reversalPeriodDays?: number;
  minimumContractDays?: number;
  isActive?: boolean;
}

export interface CommissionRuleDto {
  id: string;
  gymId: string;
  name: string;
  type: CommissionRuleType;
  percentage: number;
  priority: number;
  criteria: RuleCriteria;
  startDate?: string;
  endDate?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  usageCount: number;
}

export interface CreateCommissionRuleDto {
  name: string;
  type: CommissionRuleType;
  percentage: number;
  criteria: RuleCriteria;
  startDate?: string;
  endDate?: string;
  description?: string;
}

export interface UpdateCommissionRuleDto {
  name?: string;
  percentage?: number;
  criteria?: RuleCriteria;
  startDate?: string;
  endDate?: string;
  description?: string;
  isActive?: boolean;
}

export interface ReorderCommissionRulesDto {
  ruleIds: string[];
}

export interface CommissionRuleFiltersDto {
  type?: CommissionRuleType;
  isActive?: boolean;
  search?: string;
  page: number;
  limit: number;
}

export interface CommissionCalculationDto {
  id: string;
  gymId: string;
  contractId: string;
  collaboratorId: string;
  ruleId: string | null;
  percentage: number;
  baseAmount: number;
  amount: number;
  status: CommissionStatus;
  saleDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface CommissionFiltersDto {
  status?: CommissionStatus;
  collaboratorId?: string;
  startDate?: string;
  endDate?: string;
  page: number;
  limit: number;
}

export interface CommissionSummaryDto {
  period: {
    start: string;
    end: string;
  };
  totals: {
    totalCommissions: number;
    totalContracts: number;
    averageCommission: number;
    totalPaid: number;
    totalPending: number;
  };
  byStatus: {
    status: CommissionStatus;
    count: number;
    total: number;
  }[];
}

export interface SimulateCommissionDto {
  collaboratorId: string;
  planId: string;
  baseAmount: number;
  saleDate: string;
}

export interface CommissionSimulationDto {
  percentage: number;
  baseAmount: number;
  amount: number;
  appliedRule: {
    id: string;
    name: string;
    type: CommissionRuleType;
    percentage: number;
  } | null;
  isDefault: boolean;
}

export interface CommissionComparisonDto {
  scenarios: CommissionSimulationDto[];
  bestScenario: {
    index: number;
    amount: number;
    difference: number;
  };
}

export interface ReportFiltersDto {
  startDate?: string;
  endDate?: string;
  period?: 'week' | 'month' | 'quarter' | 'year';
  ruleId?: string;
  collaboratorId?: string;
  limit?: number;
  offset?: number;
}

export interface CommissionSummaryReportDto {
  period: {
    start: string;
    end: string;
  };
  totals: {
    totalCommissions: number;
    totalContracts: number;
    averageCommission: number;
    totalPaid: number;
    totalPending: number;
  };
  byStatus: {
    status: string;
    count: number;
    total: number;
  }[];
  byRule: {
    ruleId: string;
    ruleName: string;
    ruleType: string;
    contractsCount: number;
    totalCommissions: number;
    averageCommission: number;
  }[];
  byCollaborator: {
    collaboratorId: string;
    collaboratorName: string;
    contractsCount: number;
    totalCommissions: number;
    averageCommission: number;
  }[];
  trend: {
    date: string;
    commissions: number;
    contracts: number;
  }[];
}

export interface RuleReportDto {
  ruleId: string;
  ruleName: string;
  ruleType: string;
  percentage: number;
  contractsCount: number;
  totalCommissions: number;
  averageCommission: number;
  minCommission: number;
  maxCommission: number;
  effectiveness: {
    usageRate: number;
    conversionImpact: number;
    roi: number;
  };
}

export interface CollaboratorReportDto {
  collaboratorId: string;
  collaboratorName: string;
  roleName: string;
  contractsCount: number;
  totalCommissions: number;
  averageCommission: number;
  totalPaid: number;
  totalPending: number;
  performance: {
    rank: number;
    percentile: number;
    comparisonWithAverage: number;
  };
  rulesUsed: {
    ruleId: string;
    ruleName: string;
    count: number;
    total: number;
  }[];
}

export interface PromotionReportFiltersDto {
  promotionId?: string;
  status?: 'active' | 'scheduled' | 'expired';
  startDate?: string;
  endDate?: string;
}

export interface PromotionReportDto {
  promotionId: string;
  promotionName: string;
  percentage: number;
  startDate: string;
  endDate: string;
  status: string;
  impact: {
    contractsCreated: number;
    totalCommissions: number;
    averageCommission: number;
    comparisonWithNormal: {
      normalCommissions: number;
      promotionalCommissions: number;
      additionalCost: number;
      percentageIncrease: number;
    };
    salesIncrease: {
      contractsBeforePromotion: number;
      contractsDuringPromotion: number;
      percentageIncrease: number;
    };
    roi: {
      additionalRevenue: number;
      additionalCost: number;
      netBenefit: number;
      roiPercentage: number;
    };
  };
}

export interface CommissionHistoryFiltersDto {
  entityType?: CommissionEntityType;
  changeType?: CommissionChangeType;
  startDate?: string;
  endDate?: string;
  page: number;
  limit: number;
}

export interface CommissionHistoryDto {
  id: string;
  configId: string | null;
  ruleId: string | null;
  changeType: CommissionChangeType;
  entityType: CommissionEntityType;
  previousValue: any;
  newValue: any;
  changedBy: {
    id: string;
    name: string;
    email: string;
  };
  changedAt: string;
  reason: string | null;
  impact: string | null;
}

export interface PaginatedCommissionResult<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}
